import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@ohpen/enso-ui';

import { ConfigTableActions, LoadFailed } from '@config/components';
import { useConfigContext } from '@config/context';
import type { ParameterEntry } from '@config/interfaces';
import { CONFIG_PAGINATION, CONFIG_SCROLL } from '@config/utils';
import { DATA_TYPE_LIST_NAME, parameterColumns, SOURCE_LIST_NAME } from '../tableConfig';

export const ParametersList = (): ReactElement => {
  const { t } = useTranslation();
  const { getParameters } = useConfigContext();
  const [parameters, setParameters] = useState<ParameterEntry[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    if (!getParameters) return;

    let cancelled = false;
    setIsLoading(true);
    setHasFailed(false);
    getParameters()
      .then(result => {
        if (!cancelled) setParameters(result);
      })
      .catch(() => {
        if (!cancelled) setHasFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [getParameters]);

  const dataSource = useMemo(
    () => [...(parameters ?? [])].sort((a, b) => a.source.localeCompare(b.source) || a.name.localeCompare(b.name)),
    [parameters],
  );

  const dataTypes = useMemo(
    () => ({
      text: t('config.parameters.dataTypes.text'),
      number: t('config.parameters.dataTypes.number'),
      boolean: t('config.parameters.dataTypes.boolean'),
      list: t('config.parameters.dataTypes.list'),
    }),
    [t],
  );

  const valueLists = useMemo(() => {
    const sources: Record<string, string> = {};
    dataSource.forEach(row => {
      sources[row.source] = row.source;
    });

    return { [SOURCE_LIST_NAME]: sources, [DATA_TYPE_LIST_NAME]: dataTypes };
  }, [dataSource, dataTypes]);

  if (hasFailed) return <LoadFailed />;

  return (
    <Table
      id="configParameters"
      rowKey="key"
      dataSource={dataSource}
      columns={parameterColumns}
      valueLists={valueLists}
      loading={isLoading}
      pagination={CONFIG_PAGINATION}
      scroll={CONFIG_SCROLL}
      resizableColumns
      itemsLabel={t('config.sections.parameters')}
    >
      <ConfigTableActions />
    </Table>
  );
};
